/**
 * Elite Batch 23 Part 2 — Sales & Lead Management Workflows (19-35)
 *
 * DESIGN RULES:
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers have EMPTY inputFields — an AI extraction node follows
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Node names must be descriptive (e.g., "HubSpot: Fetch contact profile", not "API Call")
 * 7. Each workflow needs 8 nodes with proper edges
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 23 PART 2 — Sales & Lead Management Workflows (19-35)
// =============================================================================

export const ELITE_BATCH23_PART2: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 19. Churn-Risk Account to Save Play — Pattern L
  // ===================================================================
  "Churn-Risk Account to Save Play": {
    nodes: [
      trigger("Health Score: Account Risk Alert", "webhook_app", {
        source: "CustomerSuccess",
        event: "health_score.dropped",
        howItStarts:
          "Fires when an account health score drops below a critical threshold, indicating potential churn risk that requires immediate intervention",
        setup:
          "Configure your customer health scoring platform (ChurnZero, Gainsight, or custom) to fire a webhook when an account score drops below 40. Include account ID, current score, previous score, and risk factors.",
        inputFields: [],
      }),

      n("2", "hitApi", "HubSpot: Fetch account details and ARR", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/companies/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"name","operator":"CONTAINS_TOKEN","value":"{{{1.body.account_name}}}"}]}]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.hs_object_id",
              customName: "hubspot_company_id",
              type: "string",
            },
            {
              name: "results[0].properties.name",
              customName: "account_name",
              type: "string",
            },
            {
              name: "results[0].properties.annualrevenue",
              customName: "account_arr",
              type: "number",
            },
            {
              name: "results[0].properties.hubspot_owner_id",
              customName: "csm_id",
              type: "string",
            },
            {
              name: "results[0].properties.industry",
              customName: "industry",
              type: "string",
            },
          ],
        },
      }),

      aiNode(
        "3",
        "OpenAI: Analyze risk signals and recommend save strategy",
        520,
        "You are a customer retention strategist. Analyze the churn risk signals for this account and recommend the most effective save strategy based on the risk factors, account value, and industry context.",
        "Analyze churn risk for account: {account_name}. Webhook data: {{{1.body}}}. ARR: ${account_arr}. Industry: {industry}. Return JSON with: health_score, previous_score, score_drop, risk_factors (comma-separated), primary_risk_driver (usage_decline/support_issues/champion_change/competitive_threat/budget_cut), save_strategy (executive_outreach/success_plan_reset/feature_education/incentive_offer/escalation), urgency (critical/high/medium), win_back_confidence (high/medium/low).",
        [
          { field: "health_score", as: "health_score", type: "number" },
          { field: "score_drop", as: "score_drop", type: "number" },
          { field: "risk_factors", as: "risk_factors", type: "string" },
          {
            field: "primary_risk_driver",
            as: "primary_risk_driver",
            type: "string",
          },
          { field: "save_strategy", as: "save_strategy", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          {
            field: "win_back_confidence",
            as: "win_back_confidence",
            type: "string",
          },
        ],
      ),

      n("4", "runCode", "Select save play based on ARR and risk driver", 760, {
        runCodeDetails: {
          code: `const isHighValue = account_arr > 50000;
const escalationNeeded = isHighValue && urgency === 'critical';
const playbook = isHighValue
  ? (primary_risk_driver === 'champion_change' ? 'exec_realignment' : primary_risk_driver === 'competitive_threat' ? 'competitive_displacement_defense' : 'white_glove_recovery')
  : (primary_risk_driver === 'usage_decline' ? 'reactivation_campaign' : 'standard_save_play');
const responseWindow = urgency === 'critical' ? '24h' : urgency === 'high' ? '48h' : '5 business days';
return { is_high_value: isHighValue, escalation_needed: escalationNeeded, playbook: playbook, response_window: responseWindow };`,
          variables: [
            {
              name: "is_high_value",
              customName: "is_high_value",
              type: "boolean",
            },
            {
              name: "escalation_needed",
              customName: "escalation_needed",
              type: "boolean",
            },
            { name: "playbook", customName: "playbook", type: "string" },
            {
              name: "response_window",
              customName: "response_window",
              type: "string",
            },
          ],
        },
      }),

      n("5", "condition", "Is this a high-value account (ARR > $50K)?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "is_high_value", operator: "equals", value: "true" },
          ],
        },
      }),

      slackNode(
        "6",
        "Slack: Alert CSM and manager with save play brief",
        1240,
        ":rotating_light: *CHURN RISK — {urgency} Priority*\n\n*Account:* {account_name}\n*ARR:* ${account_arr} | Industry: {industry}\n*Health Score:* {health_score} (dropped {score_drop} pts)\n*Primary Risk:* {primary_risk_driver}\n*Risk Factors:* {risk_factors}\n\n*Save Play:* {playbook}\n*Strategy:* {save_strategy}\n*Confidence:* {win_back_confidence}\n*Response Window:* {response_window}\n*Escalation Needed:* {escalation_needed}\n\nPlease execute the save play within {response_window}.",
      ),

      emailNode(
        "7",
        "SendGrid: Send save play brief to CSM",
        1480,
        "csm-team@company.com",
        "URGENT: Churn Risk — {account_name} (${account_arr} ARR)",
        "Churn Risk Alert\n\nAccount: {account_name}\nARR: ${account_arr}\nHealth Score: {health_score} (dropped {score_drop} points)\nPrimary Risk Driver: {primary_risk_driver}\nRisk Factors: {risk_factors}\n\nRecommended Save Play: {playbook}\nStrategy: {save_strategy}\nWin-Back Confidence: {win_back_confidence}\n\nAction Required: Execute save play within {response_window}.\n\nSteps:\n1. Review account health dashboard and recent support tickets\n2. Schedule internal strategy call with CSM + manager\n3. Prepare customer outreach with personalized value recap\n4. Execute the {playbook} playbook\n\n— RevOps Automation",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log churn risk and save play",
        1720,
        "Churn_Risk",
        `[["{account_name}","{account_arr}","{health_score}","{score_drop}","{primary_risk_driver}","{playbook}","{urgency}","{win_back_confidence}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Monitors account health score drops, fetches account data from HubSpot, uses AI to analyze risk signals and recommend save strategies, selects the appropriate playbook based on ARR and risk driver, alerts CSMs and managers via Slack for high-value accounts, sends detailed save play briefs via email, and logs all churn risks to Google Sheets.",
  },

  // ===================================================================
  // 20. Inbound Phone Lead Logging — Pattern K
  // ===================================================================
  "Inbound Phone Lead Logging": {
    nodes: [
      trigger("Twilio: Inbound Call Completed", "webhook_app", {
        source: "Twilio",
        event: "call.completed",
        howItStarts:
          "Fires when an inbound phone call is completed via Twilio, capturing caller information, duration, and any voicemail or recording data for lead logging",
        setup:
          "In Twilio Console > Phone Numbers > Configure, set the webhook URL for call status callbacks. Enable recording and transcription. Include caller phone number, call duration, recording URL, and call direction.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Transcribe and extract caller information",
        280,
        "You are an inbound call processing specialist. Parse the Twilio call completed webhook, analyze any available transcription, and extract structured caller information for CRM logging and lead qualification.",
        "Process this Twilio inbound call: {{{1.body}}}. Return JSON with: caller_phone, caller_name (from transcription or caller ID), company_mentioned, call_duration_sec, recording_url, transcription_summary, call_type (sales_inquiry/support/billing/general), caller_intent (buying/researching/complaining/other), follow_up_needed (boolean).",
        [
          { field: "caller_phone", as: "caller_phone", type: "string" },
          { field: "caller_name", as: "caller_name", type: "string" },
          {
            field: "company_mentioned",
            as: "company_mentioned",
            type: "string",
          },
          {
            field: "call_duration_sec",
            as: "call_duration_sec",
            type: "number",
          },
          {
            field: "transcription_summary",
            as: "transcription_summary",
            type: "string",
          },
          { field: "call_type", as: "call_type", type: "string" },
          { field: "caller_intent", as: "caller_intent", type: "string" },
          {
            field: "follow_up_needed",
            as: "follow_up_needed",
            type: "boolean",
          },
        ],
      ),

      n("3", "hitApi", "HubSpot: Search or create contact from phone", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"phone","operator":"EQ","value":"{caller_phone}"}]}]',
            },
          ],
          variables: [
            { name: "total", customName: "existing_contacts", type: "number" },
            {
              name: "results[0].properties.hs_object_id",
              customName: "hubspot_contact_id",
              type: "string",
            },
            {
              name: "results[0].properties.email",
              customName: "contact_email",
              type: "string",
            },
            {
              name: "results[0].properties.lifecyclestage",
              customName: "lifecycle_stage",
              type: "string",
            },
          ],
        },
      }),

      n("4", "runCode", "Qualify lead from call context", 760, {
        runCodeDetails: {
          code: `const isSalesInquiry = call_type === 'sales_inquiry';
const isQualified = isSalesInquiry && call_duration_sec > 60 && caller_intent === 'buying';
const leadScore = (isSalesInquiry ? 30 : 0) + (call_duration_sec > 120 ? 25 : call_duration_sec > 60 ? 15 : 5) + (caller_intent === 'buying' ? 30 : caller_intent === 'researching' ? 15 : 0) + (company_mentioned ? 10 : 0);
const isNewLead = existing_contacts === 0;
return { is_qualified: isQualified, lead_score: leadScore, is_new_lead: isNewLead, needs_callback: follow_up_needed && isSalesInquiry };`,
          variables: [
            {
              name: "is_qualified",
              customName: "is_qualified",
              type: "boolean",
            },
            { name: "lead_score", customName: "lead_score", type: "number" },
            { name: "is_new_lead", customName: "is_new_lead", type: "boolean" },
            {
              name: "needs_callback",
              customName: "needs_callback",
              type: "boolean",
            },
          ],
        },
      }),

      n(
        "5",
        "condition",
        "Is this a sales inquiry requiring follow-up?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { field: "needs_callback", operator: "equals", value: "true" },
            ],
          },
        },
      ),

      slackNode(
        "6",
        "Slack: Alert sales rep with call summary",
        1240,
        ":phone: *Inbound Call — {call_type}*\n\n*Caller:* {caller_name} ({caller_phone})\n*Company:* {company_mentioned}\n*Duration:* {call_duration_sec}s\n*Intent:* {caller_intent}\n*Lead Score:* {lead_score}\n*New Lead:* {is_new_lead}\n\n*Summary:* {transcription_summary}\n\n*Action:* Callback needed — {follow_up_needed}",
      ),

      n("7", "hitApi", "HubSpot: Log call activity on contact", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/calls",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"hs_call_title":"Inbound call from {caller_name}","hs_call_body":"{transcription_summary}","hs_call_duration":"{call_duration_sec}","hs_call_direction":"INBOUND","hs_call_status":"COMPLETED","hs_call_from_number":"{caller_phone}"}',
            },
          ],
          variables: [{ name: "id", customName: "call_id", type: "string" }],
        },
      }),

      sheetsNode(
        "8",
        "Google Sheets: Log inbound call and lead data",
        1720,
        "Inbound_Calls",
        `[["{caller_name}","{caller_phone}","{company_mentioned}","{call_duration_sec}","{call_type}","{caller_intent}","{lead_score}","{is_qualified}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes inbound Twilio call completions, transcribes and extracts caller information via AI, searches HubSpot for existing contacts, qualifies the lead from call context and intent, alerts sales reps via Slack for sales inquiries needing callbacks, logs the call activity in HubSpot, and records all inbound calls to Google Sheets.",
  },

  // ===================================================================
  // 21. Referral Form to Partner Follow-Up — Pattern K
  // ===================================================================
  "Referral Form to Partner Follow-Up": {
    nodes: [
      trigger("Web Form: Referral Submitted", "webhook_form", {
        source: "WebForm",
        event: "referral.submitted",
        howItStarts:
          "Fires when a partner or customer submits a referral form, providing a new lead along with the referring partner's details for attribution and follow-up",
        setup:
          "Configure your referral program form to POST submissions to this webhook. Include fields for referred contact name, email, company, referrer name, referrer email, partner ID, and referral notes.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract referral and partner details",
        280,
        "You are a partner referral program coordinator. Parse the referral form submission and extract structured data about both the referred prospect and the referring partner for proper attribution and follow-up.",
        "Extract from this referral form submission: {{{1.body}}}. Return JSON with: referred_name, referred_email, referred_company, referred_phone, referrer_name, referrer_email, partner_id, referral_notes, referral_quality (hot/warm/cold based on notes), product_interest.",
        [
          { field: "referred_name", as: "referred_name", type: "string" },
          { field: "referred_email", as: "referred_email", type: "string" },
          { field: "referred_company", as: "referred_company", type: "string" },
          { field: "referrer_name", as: "referrer_name", type: "string" },
          { field: "referrer_email", as: "referrer_email", type: "string" },
          { field: "partner_id", as: "partner_id", type: "string" },
          { field: "referral_quality", as: "referral_quality", type: "string" },
          { field: "product_interest", as: "product_interest", type: "string" },
        ],
      ),

      n(
        "3",
        "hitApi",
        "HubSpot: Create referral contact with attribution",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"email":"{referred_email}","firstname":"{referred_name}","company":"{referred_company}","lead_source":"partner_referral","referral_partner":"{referrer_name}","referral_partner_id":"{partner_id}","hs_lead_status":"NEW"}',
              },
            ],
            variables: [
              { name: "id", customName: "hubspot_contact_id", type: "string" },
            ],
          },
        },
      ),

      n("4", "runCode", "Match referrer to active partner program", 760, {
        runCodeDetails: {
          code: `const isActivePartner = partner_id && partner_id.length > 0;
const partnerTier = isActivePartner ? 'verified' : 'unverified';
const referralBonus = referral_quality === 'hot' ? 'priority_processing' : 'standard_processing';
const followUpPriority = referral_quality === 'hot' ? 'high' : referral_quality === 'warm' ? 'medium' : 'low';
return { is_active_partner: isActivePartner, partner_tier: partnerTier, referral_bonus: referralBonus, follow_up_priority: followUpPriority };`,
          variables: [
            {
              name: "is_active_partner",
              customName: "is_active_partner",
              type: "boolean",
            },
            {
              name: "partner_tier",
              customName: "partner_tier",
              type: "string",
            },
            {
              name: "referral_bonus",
              customName: "referral_bonus",
              type: "string",
            },
            {
              name: "follow_up_priority",
              customName: "follow_up_priority",
              type: "string",
            },
          ],
        },
      }),

      n("5", "condition", "Is referrer an active partner?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "is_active_partner", operator: "equals", value: "true" },
          ],
        },
      }),

      emailNode(
        "6",
        "SendGrid: Thank partner for referral",
        1240,
        "{referrer_email}",
        "Thanks for the referral, {referrer_name}!",
        "Hi {referrer_name},\n\nThank you for referring {referred_name} at {referred_company} to us! We truly appreciate your trust and support.\n\nHere's what happens next:\n- We'll reach out to {referred_name} within 24 hours\n- You'll receive status updates as the referral progresses\n- Your referral bonus will be processed upon successful conversion\n\nPartner ID: {partner_id}\nReferral Quality: {referral_quality}\nProcessing: {referral_bonus}\n\nKeep the great referrals coming!\n\nBest,\nThe Partnerships Team",
      ),

      slackNode(
        "7",
        "Slack: Notify partnerships team",
        1480,
        ":handshake: *New Partner Referral*\n\n*Referred:* {referred_name} ({referred_email})\n*Company:* {referred_company}\n*Product Interest:* {product_interest}\n*Quality:* {referral_quality}\n\n*Referrer:* {referrer_name} ({referrer_email})\n*Partner ID:* {partner_id}\n*Partner Tier:* {partner_tier}\n*Priority:* {follow_up_priority}\n\nHubSpot: https://app.hubspot.com/contacts/{hubspot_contact_id}",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log referral for partner attribution",
        1720,
        "Referrals",
        `[["{referred_name}","{referred_email}","{referred_company}","{referrer_name}","{partner_id}","{referral_quality}","{follow_up_priority}","{product_interest}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes partner referral form submissions, extracts referral and partner details via AI, creates HubSpot contacts with referral attribution, validates the partner status, sends thank-you emails to active partners, notifies the partnerships team via Slack, and logs all referrals to Google Sheets for partner attribution tracking.",
  },

  // ===================================================================
  // 22. Event Booth Scan to CRM + Nurture — Pattern I
  // ===================================================================
  "Event Booth Scan to CRM + Nurture": {
    nodes: [
      trigger("Event: Badge Scan at Booth", "webhook_app", {
        source: "EventPlatform",
        event: "badge.scanned",
        howItStarts:
          "Fires when an attendee badge is scanned at your event booth or kiosk, capturing contact info from the event registration system",
        setup:
          "Configure your event badge scanning app (Bizzabo, Cvent, or custom) to POST scans to this webhook. Include attendee name, email, company, title, and any booth interaction notes from the scanning rep.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract attendee details from badge scan",
        280,
        "You are an event lead capture specialist. Parse the badge scan webhook and extract structured attendee information. Infer interest level from the event context and any interaction notes captured during the scan.",
        "Extract from this event badge scan: {{{1.body}}}. Return JSON with: attendee_email, attendee_name, company_name, job_title, event_name, booth_rep_notes, interest_level (hot/warm/cold), product_interest, conversation_topic, wants_demo (boolean based on notes).",
        [
          { field: "attendee_email", as: "attendee_email", type: "string" },
          { field: "attendee_name", as: "attendee_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "event_name", as: "event_name", type: "string" },
          { field: "interest_level", as: "interest_level", type: "string" },
          { field: "product_interest", as: "product_interest", type: "string" },
          { field: "wants_demo", as: "wants_demo", type: "boolean" },
        ],
      ),

      n("3", "hitApi", "HubSpot: Search or create event contact", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"email":"{attendee_email}","firstname":"{attendee_name}","company":"{company_name}","jobtitle":"{job_title}","lead_source":"event_booth","latest_event":"{event_name}","hs_lead_status":"NEW"}',
            },
          ],
          variables: [
            { name: "id", customName: "hubspot_contact_id", type: "string" },
          ],
        },
      }),

      n("4", "hitApi", "Clearbit: Enrich event lead with firmographics", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={company_name}",
          headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
          variables: [
            { name: "name", customName: "enriched_company", type: "string" },
            {
              name: "metrics.employees",
              customName: "employee_count",
              type: "number",
            },
            {
              name: "category.industry",
              customName: "industry",
              type: "string",
            },
            {
              name: "metrics.annualRevenue",
              customName: "annual_revenue",
              type: "number",
            },
          ],
        },
      }),

      n("5", "runCode", "Score event lead and tag engagement", 1000, {
        runCodeDetails: {
          code: `const interestScore = interest_level === 'hot' ? 40 : interest_level === 'warm' ? 25 : 10;
const titleScore = job_title && (job_title.includes('VP') || job_title.includes('Director') || job_title.includes('CXO')) ? 25 : 10;
const companyScore = employee_count > 200 ? 20 : employee_count > 50 ? 10 : 5;
const demoBonus = wants_demo ? 15 : 0;
const eventScore = interestScore + titleScore + companyScore + demoBonus;
return { event_score: eventScore, is_hot_lead: eventScore > 65, event_tag: 'event_' + event_name.toLowerCase().replace(/\\s+/g, '_').substring(0, 25) };`,
          variables: [
            { name: "event_score", customName: "event_score", type: "number" },
            { name: "is_hot_lead", customName: "is_hot_lead", type: "boolean" },
            { name: "event_tag", customName: "event_tag", type: "string" },
          ],
        },
      }),

      n("6", "condition", "Is this a hot event lead?", 1240, {
        conditionDetails: {
          conditions: [
            { field: "is_hot_lead", operator: "equals", value: "true" },
          ],
        },
      }),

      slackNode(
        "7",
        "Slack: Alert rep with hot event lead",
        1480,
        ":fire: *Hot Event Lead — {event_name}*\n\n*Attendee:* {attendee_name} ({attendee_email})\n*Title:* {job_title}\n*Company:* {enriched_company} ({industry}, {employee_count} emp)\n*Interest:* {interest_level} | Score: {event_score}\n*Product:* {product_interest}\n*Wants Demo:* {wants_demo}\n\nFollow up within 24 hours while the event is fresh!",
      ),

      emailNode(
        "8",
        "SendGrid: Event follow-up email with resources",
        1720,
        "{attendee_email}",
        "Great meeting you at {event_name}, {attendee_name}!",
        "Hi {attendee_name},\n\nIt was wonderful connecting with you at {event_name}! Thank you for stopping by our booth.\n\nBased on our conversation about {product_interest}, I wanted to share some resources you might find valuable:\n\n1. Product deep-dive for {industry} companies\n2. Customer success stories from companies like {enriched_company}\n3. ROI calculator tailored to your use case\n\nWould you be interested in a personalized demo? I'd love to show you how we can help {enriched_company}.\n\nFeel free to book time directly: [Calendar Link]\n\nLooking forward to continuing our conversation!\n\nBest,\nThe Sales Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Captures event booth badge scans, extracts attendee details and interest level via AI, creates HubSpot contacts with event attribution, enriches with Clearbit firmographics, scores event leads based on interest and fit, alerts reps via Slack for hot leads, and sends personalized follow-up emails with event-specific resources.",
  },

  // ===================================================================
  // 23. Lead Dedupe and Merge Workflow — Pattern K
  // ===================================================================
  "Lead Dedupe and Merge Workflow": {
    nodes: [
      trigger("Schedule: Daily Lead Deduplication Scan", "schedule", {
        schedule: "Every day at 6:00 AM",
        howItStarts:
          "Runs daily to scan recent leads in HubSpot for duplicates based on email and domain matching, flagging or merging contacts to keep the CRM clean",
        setup:
          "Configure this workflow to run daily at 6 AM. It queries HubSpot for contacts created in the last 24 hours and runs duplicate detection logic against the existing contact database.",
        inputFields: [],
      }),

      n("2", "hitApi", "HubSpot: Fetch leads created in last 24 hours", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"createdate","operator":"GTE","value":"' +
                (Date.now() - 86400000) +
                '"}]}]',
            },
            {
              key: "properties",
              value:
                '["email","firstname","lastname","company","phone","createdate","hs_object_id"]',
            },
            { key: "limit", value: "100" },
          ],
          variables: [
            { name: "total", customName: "new_contacts_count", type: "number" },
            { name: "results", customName: "recent_contacts", type: "string" },
          ],
        },
      }),

      n(
        "3",
        "runCode",
        "Find duplicates by email domain and name similarity",
        520,
        {
          runCodeDetails: {
            code: `const contacts = typeof recent_contacts === 'string' ? JSON.parse(recent_contacts || '[]') : (recent_contacts || []);
const emailDomains = {};
let duplicateCount = 0;
let duplicatePairs = [];
contacts.forEach(c => {
  const email = c.properties?.email || '';
  const domain = email.split('@')[1] || '';
  if (emailDomains[email]) { duplicateCount++; duplicatePairs.push(email); }
  emailDomains[email] = (emailDomains[email] || 0) + 1;
});
return { duplicate_count: duplicateCount, duplicate_emails: duplicatePairs.join(', '), scan_total: new_contacts_count, domains_checked: Object.keys(emailDomains).length };`,
            variables: [
              {
                name: "duplicate_count",
                customName: "duplicate_count",
                type: "number",
              },
              {
                name: "duplicate_emails",
                customName: "duplicate_emails",
                type: "string",
              },
              { name: "scan_total", customName: "scan_total", type: "number" },
              {
                name: "domains_checked",
                customName: "domains_checked",
                type: "number",
              },
            ],
          },
        },
      ),

      aiNode(
        "4",
        "OpenAI: Confirm match confidence for flagged duplicates",
        760,
        "You are a CRM data quality specialist. Analyze the flagged duplicate contacts and assess match confidence. Consider email exact matches, name similarity, and company matching to determine true duplicates vs. different people at the same company.",
        "Analyze these potential duplicates from HubSpot: Duplicate emails: {duplicate_emails}. Total scanned: {scan_total}. Domains checked: {domains_checked}. Return JSON with: confirmed_duplicates (number), high_confidence_matches (number, confidence > 90%), medium_confidence_matches (number, 70-90%), merge_recommendation (merge/review/skip), summary (description of findings).",
        [
          {
            field: "confirmed_duplicates",
            as: "confirmed_duplicates",
            type: "number",
          },
          {
            field: "high_confidence_matches",
            as: "high_confidence",
            type: "number",
          },
          {
            field: "merge_recommendation",
            as: "merge_recommendation",
            type: "string",
          },
          { field: "summary", as: "dedup_summary", type: "string" },
        ],
      ),

      n(
        "5",
        "condition",
        "Are there high-confidence duplicates to merge?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { field: "high_confidence", operator: "greaterThan", value: "0" },
            ],
          },
        },
      ),

      n("6", "hitApi", "HubSpot: Merge confirmed duplicate contacts", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/merge",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "mergeInput",
              value:
                '{"primaryObjectId":"{primary_contact_id}","objectIdToMerge":"{duplicate_contact_id}"}',
            },
          ],
          variables: [
            {
              name: "mergedContactId",
              customName: "merged_contact_id",
              type: "string",
            },
          ],
        },
      }),

      slackNode(
        "7",
        "Slack: Send dedup scan report",
        1480,
        ":broom: *Daily Lead Dedup Report*\n\n*Contacts Scanned:* {scan_total}\n*Domains Checked:* {domains_checked}\n*Duplicates Found:* {duplicate_count}\n*High Confidence:* {high_confidence}\n*Recommendation:* {merge_recommendation}\n\n*Summary:* {dedup_summary}\n\n*Duplicate Emails:* {duplicate_emails}",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log dedup scan results",
        1720,
        "Dedup_Log",
        `[["{scan_total}","{domains_checked}","{duplicate_count}","{high_confidence}","{merge_recommendation}","{dedup_summary}","${new Date().toISOString()}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Runs daily to scan recent HubSpot contacts for duplicates, identifies matches by email and domain, uses AI to confirm match confidence and recommend merge actions, auto-merges high-confidence duplicates, sends a daily dedup report to Slack, and logs all scan results to Google Sheets for data quality tracking.",
  },

  // ===================================================================
  // 24. Lead Source Attribution Backfill — Pattern K
  // ===================================================================
  "Lead Source Attribution Backfill": {
    nodes: [
      trigger("Schedule: Weekly Lead Source Audit", "schedule", {
        schedule: "Every Monday at 7:00 AM",
        howItStarts:
          "Runs weekly to find HubSpot leads with missing or incomplete lead source attribution and backfills the data from UTM parameters, referrer data, and contextual analysis",
        setup:
          "Configure to run every Monday at 7 AM. The workflow queries HubSpot for contacts missing lead_source data and attempts to reconstruct attribution from available UTM and referrer signals.",
        inputFields: [],
      }),

      n(
        "2",
        "hitApi",
        "HubSpot: Fetch leads with missing source attribution",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"lead_source","operator":"NOT_HAS_PROPERTY"}]}]',
              },
              {
                key: "properties",
                value:
                  '["email","firstname","company","hs_analytics_source","hs_analytics_first_url","utm_source","utm_medium","utm_campaign","hs_object_id"]',
              },
              { key: "limit", value: "100" },
            ],
            variables: [
              {
                name: "total",
                customName: "missing_source_count",
                type: "number",
              },
              {
                name: "results[0].properties.hs_object_id",
                customName: "sample_contact_id",
                type: "string",
              },
              {
                name: "results[0].properties.hs_analytics_source",
                customName: "analytics_source",
                type: "string",
              },
              {
                name: "results[0].properties.utm_source",
                customName: "utm_source",
                type: "string",
              },
              {
                name: "results[0].properties.utm_medium",
                customName: "utm_medium",
                type: "string",
              },
            ],
          },
        },
      ),

      n(
        "3",
        "runCode",
        "Check UTM and referrer data for attribution clues",
        520,
        {
          runCodeDetails: {
            code: `const hasUtm = utm_source && utm_source.length > 0;
const hasAnalytics = analytics_source && analytics_source.length > 0;
const inferredSource = hasUtm ? utm_source : hasAnalytics ? analytics_source : 'unknown';
const inferredMedium = utm_medium || (hasAnalytics ? 'organic' : 'direct');
const canResolve = hasUtm || hasAnalytics;
const resolvableCount = canResolve ? Math.ceil(missing_source_count * 0.7) : 0;
return { inferred_source: inferredSource, inferred_medium: inferredMedium, can_resolve: canResolve, resolvable_estimate: resolvableCount };`,
            variables: [
              {
                name: "inferred_source",
                customName: "inferred_source",
                type: "string",
              },
              {
                name: "inferred_medium",
                customName: "inferred_medium",
                type: "string",
              },
              {
                name: "can_resolve",
                customName: "can_resolve",
                type: "boolean",
              },
              {
                name: "resolvable_estimate",
                customName: "resolvable_estimate",
                type: "number",
              },
            ],
          },
        },
      ),

      aiNode(
        "4",
        "OpenAI: Infer lead source from available context",
        760,
        "You are a marketing attribution analyst. Given the available UTM parameters, analytics source data, and page URLs, determine the most likely lead source category for contacts missing attribution.",
        "Analyze these attribution signals: UTM source: {utm_source}, UTM medium: {utm_medium}, Analytics source: {analytics_source}. Total contacts missing source: {missing_source_count}. Return JSON with: source_category (organic_search/paid_search/social_organic/social_paid/email/referral/direct/event/partner), confidence_level (high/medium/low), attribution_method (utm/analytics/inferred), notes (explanation of attribution logic).",
        [
          { field: "source_category", as: "source_category", type: "string" },
          { field: "confidence_level", as: "confidence_level", type: "string" },
          {
            field: "attribution_method",
            as: "attribution_method",
            type: "string",
          },
          { field: "notes", as: "attribution_notes", type: "string" },
        ],
      ),

      n(
        "5",
        "condition",
        "Was the lead source successfully determined?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { field: "can_resolve", operator: "equals", value: "true" },
            ],
          },
        },
      ),

      n(
        "6",
        "hitApi",
        "HubSpot: Update contacts with backfilled lead source",
        1240,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{sample_contact_id}",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"lead_source":"{source_category}","lead_source_detail":"{inferred_source}/{inferred_medium}","attribution_method":"{attribution_method}","attribution_confidence":"{confidence_level}"}',
              },
            ],
            variables: [
              { name: "id", customName: "updated_contact_id", type: "string" },
            ],
          },
        },
      ),

      slackNode(
        "7",
        "Slack: Send weekly attribution backfill report",
        1480,
        ":chart_with_upwards_trend: *Weekly Attribution Backfill Report*\n\n*Contacts Missing Source:* {missing_source_count}\n*Resolvable:* ~{resolvable_estimate}\n*Primary Source Category:* {source_category}\n*Attribution Method:* {attribution_method}\n*Confidence:* {confidence_level}\n\n*Notes:* {attribution_notes}\n\nReview and approve batch updates in the CRM.",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log attribution backfill results",
        1720,
        "Attribution_Backfill",
        `[["{missing_source_count}","{resolvable_estimate}","{source_category}","{attribution_method}","{confidence_level}","{attribution_notes}","${new Date().toISOString()}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Runs weekly to audit HubSpot contacts with missing lead source attribution, checks UTM and analytics data for attribution clues, uses AI to infer source categories from available context, backfills lead source data on resolvable contacts, sends a weekly report to Slack, and logs all backfill results to Google Sheets.",
  },

  // ===================================================================
  // 25. Gmail Sales Inquiry to Pipeline — Pattern K
  // ===================================================================
  "Gmail Sales Inquiry to Pipeline": {
    nodes: [
      trigger("Email: New Message to Sales Inbox", "email", {
        source: "Gmail",
        event: "email.received",
        howItStarts:
          "Fires when a new email arrives in the sales@ shared inbox, triggering automated classification, lead creation, and routing for genuine sales inquiries",
        setup:
          "Configure Gmail forwarding or a Google Workspace rule to POST new emails to sales@ to this webhook. Include sender email, name, subject, body text, and any attachments metadata.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Classify email as sales inquiry",
        280,
        "You are a sales inbox triage specialist. Analyze the incoming email and determine if it is a genuine sales inquiry, a spam/marketing email, or a support/billing request. Extract key details from legitimate sales inquiries.",
        "Classify this email to sales@: {{{1.body}}}. Return JSON with: sender_email, sender_name, subject_line, is_sales_inquiry (boolean), classification (sales_inquiry/support_request/spam/vendor_pitch/newsletter), if sales inquiry also: company_name, request_type (demo/pricing/partnership/general), urgency (high/medium/low), key_requirements (what they're looking for).",
        [
          { field: "sender_email", as: "sender_email", type: "string" },
          { field: "sender_name", as: "sender_name", type: "string" },
          {
            field: "is_sales_inquiry",
            as: "is_sales_inquiry",
            type: "boolean",
          },
          { field: "classification", as: "classification", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "request_type", as: "request_type", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "key_requirements", as: "key_requirements", type: "string" },
        ],
      ),

      aiNode(
        "3",
        "OpenAI: Extract company and contact details",
        520,
        "You are a B2B lead data extraction specialist. From the email content and sender information, extract as much structured data about the prospect and their company as possible for CRM entry.",
        "Extract lead details from: Sender: {sender_name} ({sender_email}). Company: {company_name}. Requirements: {key_requirements}. Request type: {request_type}. Return JSON with: first_name, last_name, email_domain, estimated_company_size (small/medium/large from email signature or content clues), industry_guess, budget_mentioned (boolean), timeline_mentioned.",
        [
          { field: "first_name", as: "first_name", type: "string" },
          { field: "last_name", as: "last_name", type: "string" },
          { field: "email_domain", as: "email_domain", type: "string" },
          {
            field: "estimated_company_size",
            as: "company_size",
            type: "string",
          },
          {
            field: "budget_mentioned",
            as: "budget_mentioned",
            type: "boolean",
          },
          {
            field: "timeline_mentioned",
            as: "timeline_mentioned",
            type: "string",
          },
        ],
      ),

      n("4", "hitApi", "HubSpot: Create contact and deal from inquiry", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"email":"{sender_email}","firstname":"{first_name}","lastname":"{last_name}","company":"{company_name}","lead_source":"inbound_email","hs_lead_status":"NEW"}',
            },
          ],
          variables: [
            { name: "id", customName: "hubspot_contact_id", type: "string" },
          ],
        },
      }),

      n("5", "runCode", "Route inquiry by territory and request type", 1000, {
        runCodeDetails: {
          code: `const routeMap = { demo: 'ae_team', pricing: 'ae_team', partnership: 'partnerships_team', general: 'sdr_team' };
const assignedTeam = routeMap[request_type] || 'sdr_team';
const slaHours = urgency === 'high' ? 2 : urgency === 'medium' ? 8 : 24;
const autoReplyType = is_sales_inquiry ? 'sales_acknowledgment' : 'general_redirect';
return { assigned_team: assignedTeam, sla_hours: slaHours, auto_reply_type: autoReplyType };`,
          variables: [
            {
              name: "assigned_team",
              customName: "assigned_team",
              type: "string",
            },
            { name: "sla_hours", customName: "sla_hours", type: "number" },
            {
              name: "auto_reply_type",
              customName: "auto_reply_type",
              type: "string",
            },
          ],
        },
      }),

      slackNode(
        "6",
        "Slack: Alert assigned team with inquiry details",
        1240,
        ":email: *New Sales Inquiry via Email*\n\n*From:* {sender_name} ({sender_email})\n*Company:* {company_name} ({company_size})\n*Request:* {request_type}\n*Requirements:* {key_requirements}\n*Urgency:* {urgency}\n*Budget Mentioned:* {budget_mentioned}\n*Timeline:* {timeline_mentioned}\n\n*Assigned:* {assigned_team}\n*SLA:* Respond within {sla_hours}h\n\nHubSpot: https://app.hubspot.com/contacts/{hubspot_contact_id}",
      ),

      emailNode(
        "7",
        "SendGrid: Auto-reply acknowledging inquiry",
        1480,
        "{sender_email}",
        "We received your inquiry, {first_name}!",
        "Hi {first_name},\n\nThank you for reaching out to us! We received your {request_type} inquiry regarding {key_requirements}.\n\nA member of our {assigned_team} will follow up with you within {sla_hours} hours.\n\nIn the meantime, here are some resources that might be helpful:\n- Product overview and capabilities\n- Pricing guide\n- Customer case studies\n\nWe look forward to learning more about how we can help {company_name}!\n\nBest regards,\nThe Sales Team",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log sales inquiry for tracking",
        1720,
        "Email_Inquiries",
        `[["{sender_name}","{sender_email}","{company_name}","{request_type}","{urgency}","{assigned_team}","{sla_hours}","{key_requirements}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Monitors the sales@ inbox, classifies incoming emails via AI to filter genuine sales inquiries, extracts company and contact details, creates HubSpot contacts, routes to the appropriate team based on request type, alerts the team via Slack with full context, sends an auto-reply to the prospect, and logs all inquiries to Google Sheets.",
  },

  // ===================================================================
  // 26. Calendly Booking to CRM Lead — Pattern K
  // ===================================================================
  "Calendly Booking to CRM Lead": {
    nodes: [
      trigger("Calendly: New Meeting Scheduled", "webhook_app", {
        source: "Calendly",
        event: "invitee.created",
        howItStarts:
          "Fires when someone books a meeting through any Calendly scheduling link, creating or updating a CRM lead with enriched data and notifying the assigned rep",
        setup:
          "In Calendly > Integrations > Webhooks, subscribe to 'invitee.created' events for all event types. Include invitee details, event type, scheduled time, and responses to any routing questions.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract booking details and attendee info",
        280,
        "You are a meeting booking processor. Parse the Calendly invitee webhook and extract structured attendee and meeting details for CRM logging and rep preparation.",
        "Extract from this Calendly booking webhook: {{{1.body}}}. Return JSON with: attendee_email, attendee_name, company_name, meeting_type (discovery/demo/consultation/follow_up), scheduled_datetime, duration_minutes, timezone, booking_questions_summary (from any custom questions), calendly_event_url.",
        [
          { field: "attendee_email", as: "attendee_email", type: "string" },
          { field: "attendee_name", as: "attendee_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "meeting_type", as: "meeting_type", type: "string" },
          {
            field: "scheduled_datetime",
            as: "scheduled_datetime",
            type: "string",
          },
          { field: "duration_minutes", as: "duration_minutes", type: "number" },
          {
            field: "booking_questions_summary",
            as: "questions_summary",
            type: "string",
          },
        ],
      ),

      n("3", "hitApi", "HubSpot: Search or create contact", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"email":"{attendee_email}","firstname":"{attendee_name}","company":"{company_name}","lead_source":"calendly","hs_lead_status":"NEW"}',
            },
          ],
          variables: [
            { name: "id", customName: "hubspot_contact_id", type: "string" },
            {
              name: "properties.lifecyclestage",
              customName: "lifecycle_stage",
              type: "string",
            },
          ],
        },
      }),

      n("4", "hitApi", "Clearbit: Enrich contact and company data", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={attendee_email}",
          headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
          variables: [
            {
              name: "person.employment.title",
              customName: "job_title",
              type: "string",
            },
            {
              name: "person.employment.seniority",
              customName: "seniority",
              type: "string",
            },
            {
              name: "company.name",
              customName: "enriched_company",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "employee_count",
              type: "number",
            },
            {
              name: "company.category.industry",
              customName: "industry",
              type: "string",
            },
          ],
        },
      }),

      n(
        "5",
        "runCode",
        "Assign meeting owner and set prep requirements",
        1000,
        {
          runCodeDetails: {
            code: `const ownerMap = { discovery: 'sdr_pool', demo: 'ae_pool', consultation: 'solutions_pool', follow_up: 'existing_owner' };
const assignedPool = ownerMap[meeting_type] || 'sdr_pool';
const needsPrep = meeting_type === 'demo' || meeting_type === 'consultation';
const seniorityWeight = seniority === 'executive' || seniority === 'director' ? 'high_priority' : 'standard';
return { assigned_pool: assignedPool, needs_prep: needsPrep, priority: seniorityWeight };`,
            variables: [
              {
                name: "assigned_pool",
                customName: "assigned_pool",
                type: "string",
              },
              { name: "needs_prep", customName: "needs_prep", type: "boolean" },
              { name: "priority", customName: "priority", type: "string" },
            ],
          },
        },
      ),

      n("6", "hitApi", "HubSpot: Create meeting activity on contact", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/meetings",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"hs_meeting_title":"{meeting_type} with {attendee_name} at {enriched_company}","hs_meeting_start_time":"{scheduled_datetime}","hs_meeting_outcome":"SCHEDULED","hs_meeting_body":"Attendee: {attendee_name}\\nCompany: {enriched_company} ({industry}, {employee_count} emp)\\nTitle: {job_title}\\nNotes: {questions_summary}"}',
            },
          ],
          variables: [{ name: "id", customName: "meeting_id", type: "string" }],
        },
      }),

      slackNode(
        "7",
        "Slack: Notify assigned rep with meeting details",
        1480,
        ":calendar: *New Calendly Booking — {meeting_type}*\n\n*Attendee:* {attendee_name} ({attendee_email})\n*Title:* {job_title} ({seniority})\n*Company:* {enriched_company} ({industry}, {employee_count} emp)\n*Meeting:* {meeting_type} | {duration_minutes} min\n*Scheduled:* {scheduled_datetime}\n*Priority:* {priority}\n*Prep Needed:* {needs_prep}\n\n*Booking Notes:* {questions_summary}\n\nHubSpot: https://app.hubspot.com/contacts/{hubspot_contact_id}",
      ),

      emailNode(
        "8",
        "SendGrid: Send meeting prep info to attendee",
        1720,
        "{attendee_email}",
        "Your {meeting_type} is confirmed, {attendee_name}!",
        "Hi {attendee_name},\n\nYour {meeting_type} is confirmed for {scheduled_datetime} ({duration_minutes} minutes).\n\nTo make the most of our time together, here's what to expect:\n- We'll discuss your specific needs at {enriched_company}\n- We'll walk through relevant capabilities for the {industry} space\n- We'll answer any questions you have\n\nIf you'd like to share any additional context beforehand, simply reply to this email.\n\nSee you soon!\n\nBest,\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes Calendly bookings, extracts attendee and meeting details via AI, creates HubSpot contacts, enriches with Clearbit person and company data, assigns meeting ownership and sets prep requirements, logs the meeting in HubSpot, notifies the rep via Slack, and sends meeting prep information to the attendee.",
  },

  // ===================================================================
  // 27. Contact & Company Enrichment from Website — Pattern K
  // ===================================================================
  "Contact & Company Enrichment from Website": {
    nodes: [
      trigger("Website: Identified Visitor Detected", "webhook_app", {
        source: "Website",
        event: "visitor.identified",
        howItStarts:
          "Fires when your website visitor identification tool (Clearbit Reveal, Leadfeeder, or similar) identifies a company visiting your site based on IP reverse lookup",
        setup:
          "Configure your website identification tool to POST identified visitor webhooks. Include the identified company domain, pages visited, session duration, and any form fills or chatbot interactions.",
        inputFields: [],
      }),

      n("2", "hitApi", "Clearbit: Reveal company from visitor IP", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://reveal.clearbit.com/v1/companies/find?url={{{1.body.page_url}}}",
          headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
          variables: [
            {
              name: "company.name",
              customName: "company_name",
              type: "string",
            },
            {
              name: "company.domain",
              customName: "company_domain",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "employee_count",
              type: "number",
            },
            {
              name: "company.metrics.annualRevenue",
              customName: "annual_revenue",
              type: "number",
            },
            {
              name: "company.category.industry",
              customName: "industry",
              type: "string",
            },
            {
              name: "company.geo.country",
              customName: "country",
              type: "string",
            },
          ],
        },
      }),

      n("3", "hitApi", "Apollo: Find key contacts at identified company", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/mixed_people/search",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "x-api-key", value: "{apollo_api_key}" },
          ],
          body: [
            { key: "organization_domains", value: '["{company_domain}"]' },
            {
              key: "person_seniorities",
              value: '["director","vp","c_suite","manager"]',
            },
            { key: "per_page", value: "5" },
          ],
          variables: [
            {
              name: "people[0].name",
              customName: "contact_1_name",
              type: "string",
            },
            {
              name: "people[0].title",
              customName: "contact_1_title",
              type: "string",
            },
            {
              name: "people[0].email",
              customName: "contact_1_email",
              type: "string",
            },
            {
              name: "people[1].name",
              customName: "contact_2_name",
              type: "string",
            },
            {
              name: "people[1].email",
              customName: "contact_2_email",
              type: "string",
            },
            {
              name: "pagination.total_entries",
              customName: "total_contacts",
              type: "number",
            },
          ],
        },
      }),

      aiNode(
        "4",
        "OpenAI: Score relevance and recommend outreach",
        760,
        "You are a B2B intent signal analyst. Based on the company profile, website visit data, and available contacts, score the relevance of this website visitor and recommend an outreach approach.",
        "Analyze website visitor: Company: {company_name} ({industry}, {employee_count} emp, ${annual_revenue} rev). Country: {country}. Visit data: {{{1.body}}}. Top contact: {contact_1_name} — {contact_1_title}. Total contacts: {total_contacts}. Return JSON with: relevance_score (0-100), is_target_account (boolean), recommended_action (outreach/monitor/skip), outreach_channel (email/linkedin/phone), personalization_angle (what to lead with based on pages visited).",
        [
          { field: "relevance_score", as: "relevance_score", type: "number" },
          {
            field: "is_target_account",
            as: "is_target_account",
            type: "boolean",
          },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          { field: "outreach_channel", as: "outreach_channel", type: "string" },
          {
            field: "personalization_angle",
            as: "personalization_angle",
            type: "string",
          },
        ],
      ),

      n("5", "condition", "Is this a target account worth pursuing?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "is_target_account", operator: "equals", value: "true" },
          ],
        },
      }),

      n("6", "hitApi", "HubSpot: Create or update company and contacts", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/companies",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"domain":"{company_domain}","name":"{company_name}","industry":"{industry}","numberofemployees":"{employee_count}","annualrevenue":"{annual_revenue}","country":"{country}","lead_source":"website_visitor","relevance_score":"{relevance_score}"}',
            },
          ],
          variables: [
            { name: "id", customName: "hubspot_company_id", type: "string" },
          ],
        },
      }),

      slackNode(
        "7",
        "Slack: Alert SDR with identified visitor intel",
        1480,
        ":eyes: *Website Visitor Identified — Target Account*\n\n*Company:* {company_name} ({company_domain})\n*Industry:* {industry} | Employees: {employee_count} | Revenue: ${annual_revenue}\n*Country:* {country}\n*Relevance Score:* {relevance_score}/100\n\n*Top Contact:* {contact_1_name} — {contact_1_title} ({contact_1_email})\n*Contact 2:* {contact_2_name} ({contact_2_email})\n*Total Contacts:* {total_contacts}\n\n*Recommended:* {recommended_action} via {outreach_channel}\n*Angle:* {personalization_angle}",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log identified visitor for tracking",
        1720,
        "Website_Visitors",
        `[["{company_name}","{company_domain}","{industry}","{employee_count}","{relevance_score}","{is_target_account}","{contact_1_name}","{contact_1_email}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Identifies anonymous website visitors via Clearbit Reveal, finds key contacts at the identified company via Apollo, uses AI to score relevance and recommend outreach approaches, creates HubSpot company records for target accounts, alerts SDRs via Slack with full visitor intelligence and contact info, and logs all identified visitors to Google Sheets.",
  },

  // ===================================================================
  // 28. Sales Activity Digest to Manager — Pattern L
  // ===================================================================
  "Sales Activity Digest to Manager": {
    nodes: [
      trigger("Schedule: Daily Sales Activity Digest", "schedule", {
        schedule: "Every day at 6:00 PM",
        howItStarts:
          "Runs daily at 6 PM to compile a comprehensive sales activity digest for managers, summarizing team performance, pipeline changes, and areas needing attention",
        setup:
          "Configure to run daily at 6 PM local time. The workflow pulls activity data from HubSpot, computes per-rep metrics, and delivers a formatted digest to the sales manager.",
        inputFields: [],
      }),

      n("2", "hitApi", "HubSpot: Fetch today's team sales activities", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/calls/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"hs_timestamp","operator":"GTE","value":"' +
                (Date.now() - 86400000) +
                '"}]}]',
            },
            { key: "limit", value: "200" },
          ],
          variables: [
            { name: "total", customName: "total_calls_today", type: "number" },
          ],
        },
      }),

      n("3", "runCode", "Compute per-rep activity metrics", 520, {
        runCodeDetails: {
          code: `const callsTarget = 15;
const emailsTarget = 30;
const meetingsTarget = 3;
const callPerformance = total_calls_today >= callsTarget ? 'on_track' : 'below_target';
const callGap = callsTarget - total_calls_today;
const overallHealth = total_calls_today >= callsTarget * 0.8 ? 'healthy' : total_calls_today >= callsTarget * 0.5 ? 'needs_attention' : 'critical';
return { calls_today: total_calls_today, call_target: callsTarget, call_performance: callPerformance, call_gap: callGap > 0 ? callGap : 0, team_health: overallHealth };`,
          variables: [
            { name: "calls_today", customName: "calls_today", type: "number" },
            { name: "call_target", customName: "call_target", type: "number" },
            {
              name: "call_performance",
              customName: "call_performance",
              type: "string",
            },
            { name: "call_gap", customName: "call_gap", type: "number" },
            { name: "team_health", customName: "team_health", type: "string" },
          ],
        },
      }),

      aiNode(
        "4",
        "OpenAI: Generate narrative summary with highlights and concerns",
        760,
        "You are a sales operations analyst. Generate a concise daily activity narrative for the sales manager. Highlight top performers, flag underperformers, and surface any patterns or concerns that need attention.",
        "Generate a daily sales digest: Calls today: {calls_today} (target: {call_target}). Performance: {call_performance}. Team health: {team_health}. Gap: {call_gap} calls short. Return JSON with: headline (one-line summary), highlights (top 2-3 positive points), concerns (any issues needing attention), recommendation (1 specific action for the manager), performance_grade (A/B/C/D).",
        [
          { field: "headline", as: "headline", type: "string" },
          { field: "highlights", as: "highlights", type: "string" },
          { field: "concerns", as: "concerns", type: "string" },
          { field: "recommendation", as: "recommendation", type: "string" },
          {
            field: "performance_grade",
            as: "performance_grade",
            type: "string",
          },
        ],
      ),

      n("5", "condition", "Is any rep significantly below target?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "team_health", operator: "equals", value: "critical" },
          ],
        },
      }),

      slackNode(
        "6",
        "Slack: Post activity digest to #sales-management",
        1240,
        ":bar_chart: *Daily Sales Activity Digest*\n\n*{headline}*\n*Grade:* {performance_grade}\n\n*Metrics:*\n- Calls: {calls_today}/{call_target} ({call_performance})\n- Gap: {call_gap} calls below target\n- Team Health: {team_health}\n\n*Highlights:*\n{highlights}\n\n*Concerns:*\n{concerns}\n\n*Recommendation:*\n{recommendation}",
      ),

      emailNode(
        "7",
        "SendGrid: Send weekly rollup to leadership",
        1480,
        "sales-manager@company.com",
        "Daily Sales Digest: {headline}",
        "Daily Sales Activity Summary\n\nGrade: {performance_grade}\n\nKey Metrics:\n- Total Calls: {calls_today} (Target: {call_target})\n- Performance: {call_performance}\n- Team Health: {team_health}\n\nHighlights:\n{highlights}\n\nConcerns:\n{concerns}\n\nRecommended Action:\n{recommendation}\n\nFull details available in the sales dashboard.\n\n— RevOps Automation",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log daily activity metrics",
        1720,
        "Activity_Digest",
        `[["{calls_today}","{call_target}","{call_performance}","{team_health}","{performance_grade}","{headline}","${new Date().toISOString()}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Runs daily at 6 PM to compile sales team activity data from HubSpot, computes per-rep metrics against targets, generates an AI-powered narrative summary with highlights and concerns, posts the digest to Slack for the management channel, sends email rollups to leadership, and logs daily metrics to Google Sheets for trend analysis.",
  },

  // ===================================================================
  // 29. Objection Tracking from Call Transcripts — Pattern I
  // ===================================================================
  "Objection Tracking from Call Transcripts": {
    nodes: [
      trigger("Gong: Sales Call Ended", "webhook_app", {
        source: "Gong",
        event: "call.ended",
        howItStarts:
          "Fires when a Gong-recorded sales call ends, triggering automated objection extraction, categorization, and trend tracking to improve sales enablement",
        setup:
          "In Gong > Settings > Integrations > Webhooks, configure a webhook for 'Call Ended' events. Ensure the payload includes call_id, transcript, participants, deal context, and call metadata.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract objections from call transcript",
        280,
        "You are a sales conversation analyst specializing in objection detection. Analyze the call transcript to identify every objection raised by the prospect, including implied or indirect objections.",
        "Analyze this Gong call transcript for objections: {{{1.body}}}. Return JSON with: call_id, rep_name, prospect_name, prospect_company, call_duration_min, objections_found (number), objection_list (comma-separated list of objections), primary_objection (the most significant one), objection_categories (pricing/timing/competitor/authority/need/trust), rep_response_quality (strong/adequate/weak for handling the primary objection).",
        [
          { field: "call_id", as: "call_id", type: "string" },
          { field: "rep_name", as: "rep_name", type: "string" },
          { field: "prospect_company", as: "prospect_company", type: "string" },
          { field: "objections_found", as: "objections_found", type: "number" },
          { field: "objection_list", as: "objection_list", type: "string" },
          {
            field: "primary_objection",
            as: "primary_objection",
            type: "string",
          },
          {
            field: "objection_categories",
            as: "objection_categories",
            type: "string",
          },
          {
            field: "rep_response_quality",
            as: "rep_response_quality",
            type: "string",
          },
        ],
      ),

      n("3", "runCode", "Categorize objection types and detect patterns", 520, {
        runCodeDetails: {
          code: `const categories = objection_categories.split('/');
const isPricingObjection = categories.includes('pricing');
const isCompetitorObjection = categories.includes('competitor');
const needsCoaching = rep_response_quality === 'weak';
const trendTag = categories[0] || 'general';
const severity = objections_found > 3 ? 'high' : objections_found > 1 ? 'medium' : 'low';
return { is_pricing_objection: isPricingObjection, is_competitor_objection: isCompetitorObjection, needs_coaching: needsCoaching, trend_tag: trendTag, objection_severity: severity };`,
          variables: [
            {
              name: "is_pricing_objection",
              customName: "is_pricing_objection",
              type: "boolean",
            },
            {
              name: "is_competitor_objection",
              customName: "is_competitor_objection",
              type: "boolean",
            },
            {
              name: "needs_coaching",
              customName: "needs_coaching",
              type: "boolean",
            },
            { name: "trend_tag", customName: "trend_tag", type: "string" },
            {
              name: "objection_severity",
              customName: "objection_severity",
              type: "string",
            },
          ],
        },
      }),

      n("4", "hitApi", "HubSpot: Tag deal with objection data", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/notes",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"hs_note_body":"Objections from call {call_id}:\\n\\nObjections ({objections_found}): {objection_list}\\nPrimary: {primary_objection}\\nCategories: {objection_categories}\\nRep Response: {rep_response_quality}\\nSeverity: {objection_severity}","hs_timestamp":"' +
                new Date().toISOString() +
                '"}',
            },
          ],
          variables: [{ name: "id", customName: "note_id", type: "string" }],
        },
      }),

      n("5", "condition", "Was a pricing objection raised?", 1000, {
        conditionDetails: {
          conditions: [
            {
              field: "is_pricing_objection",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),

      slackNode(
        "6",
        "Slack: Alert deal desk about pricing objection",
        1240,
        ":money_with_wings: *Pricing Objection Detected*\n\n*Account:* {prospect_company}\n*Rep:* {rep_name}\n*Primary Objection:* {primary_objection}\n*All Objections:* {objection_list}\n*Rep Response Quality:* {rep_response_quality}\n*Severity:* {objection_severity}\n\nConsider preparing a competitive pricing analysis or discount approval for this deal.\nGong Call: {call_id}",
      ),

      sheetsNode(
        "7",
        "Google Sheets: Log objection for trend reporting",
        1480,
        "Objection_Trends",
        `[["{rep_name}","{prospect_company}","{objections_found}","{primary_objection}","{objection_categories}","{rep_response_quality}","{objection_severity}","{call_id}"]]`,
      ),

      emailNode(
        "8",
        "SendGrid: Send coaching note to rep if needed",
        1720,
        "{rep_name}@company.com",
        "Objection Handling Coaching: {prospect_company} call",
        "Hi {rep_name},\n\nI reviewed your recent call with {prospect_company} and wanted to share some coaching notes on objection handling.\n\nObjections Detected ({objections_found}):\n{objection_list}\n\nPrimary Objection: {primary_objection}\nYour Response Quality: {rep_response_quality}\n\nHere are some suggestions for strengthening your response to {primary_objection}:\n1. Lead with value before discussing price\n2. Use customer proof points from similar companies\n3. Reframe the objection as an investment discussion\n\nLet me know if you'd like to role-play this objection before your next call.\n\nBest,\nSales Enablement",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes Gong call recordings, extracts and categorizes objections from transcripts via AI, tags deals in HubSpot with objection data, alerts the deal desk via Slack when pricing objections are detected, logs all objections to Google Sheets for trend analysis, and sends coaching notes to reps who need help with objection handling.",
  },

  // ===================================================================
  // 30. Dormant Prospect Re-Engagement Workflow — Pattern G
  // ===================================================================
  "Dormant Prospect Re-Engagement Workflow": {
    nodes: [
      trigger("Schedule: Weekly Dormant Prospect Scan", "schedule", {
        schedule: "Every Monday at 9:00 AM",
        howItStarts:
          "Runs weekly to identify prospects in HubSpot who have been dormant for 60+ days with no engagement, and initiates a targeted re-engagement campaign",
        setup:
          "Configure to run every Monday at 9 AM. The workflow queries HubSpot for contacts with no activity in the past 60 days and generates personalized re-engagement emails based on their profile and history.",
        inputFields: [],
      }),

      n("2", "hitApi", "HubSpot: Fetch 60-day dormant prospects", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"notes_last_updated","operator":"LT","value":"' +
                (Date.now() - 60 * 86400000) +
                '"},{"propertyName":"hs_lead_status","operator":"NEQ","value":"UNQUALIFIED"},{"propertyName":"lifecyclestage","operator":"EQ","value":"lead"}]}]',
            },
            {
              key: "properties",
              value:
                '["email","firstname","company","jobtitle","lead_score","hs_object_id","annualrevenue"]',
            },
            { key: "limit", value: "50" },
          ],
          variables: [
            { name: "total", customName: "dormant_count", type: "number" },
            {
              name: "results[0].properties.email",
              customName: "prospect_email",
              type: "string",
            },
            {
              name: "results[0].properties.firstname",
              customName: "prospect_name",
              type: "string",
            },
            {
              name: "results[0].properties.company",
              customName: "company_name",
              type: "string",
            },
            {
              name: "results[0].properties.lead_score",
              customName: "lead_score",
              type: "number",
            },
            {
              name: "results[0].properties.hs_object_id",
              customName: "contact_id",
              type: "string",
            },
          ],
        },
      }),

      aiNode(
        "3",
        "OpenAI: Select personalized re-engagement angle",
        520,
        "You are a sales re-engagement specialist. For each dormant prospect, select the most effective re-engagement angle based on their profile, industry, and original lead source. Avoid generic follow-ups.",
        "Select re-engagement approach for: {prospect_name} at {company_name}. Lead score: {lead_score}. Total dormant in batch: {dormant_count}. Return JSON with: reengagement_angle (new_feature/industry_insight/case_study/event_invite/check_in), email_subject_line (personalized subject), email_hook (opening line that references something specific), value_prop (1 sentence on what they're missing), cta (specific call to action).",
        [
          {
            field: "reengagement_angle",
            as: "reengagement_angle",
            type: "string",
          },
          { field: "email_subject_line", as: "email_subject", type: "string" },
          { field: "email_hook", as: "email_hook", type: "string" },
          { field: "value_prop", as: "value_prop", type: "string" },
          { field: "cta", as: "cta", type: "string" },
        ],
      ),

      n(
        "4",
        "runCode",
        "Suppress recently contacted and compute batch priority",
        760,
        {
          runCodeDetails: {
            code: `const isHighValue = lead_score > 50;
const batchPriority = isHighValue ? 'priority' : 'standard';
const shouldSend = dormant_count > 0;
const reengageLimit = Math.min(dormant_count, 25);
return { is_high_value: isHighValue, batch_priority: batchPriority, should_send: shouldSend, reengage_limit: reengageLimit };`,
            variables: [
              {
                name: "is_high_value",
                customName: "is_high_value",
                type: "boolean",
              },
              {
                name: "batch_priority",
                customName: "batch_priority",
                type: "string",
              },
              {
                name: "should_send",
                customName: "should_send",
                type: "boolean",
              },
              {
                name: "reengage_limit",
                customName: "reengage_limit",
                type: "number",
              },
            ],
          },
        },
      ),

      n("5", "condition", "Is this a high-value dormant prospect?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "is_high_value", operator: "equals", value: "true" },
          ],
        },
      }),

      emailNode(
        "6",
        "SendGrid: Send personalized re-engagement email",
        1240,
        "{prospect_email}",
        "{email_subject}",
        "{email_hook}\n\n{value_prop}\n\nSince we last connected, we've been busy building new capabilities that I think would be particularly relevant for {company_name}:\n\n- New features tailored to companies like yours\n- Fresh customer success stories with measurable ROI\n- Updated pricing options to fit any budget\n\n{cta}\n\nWould love to reconnect and share what's new. Would a 15-minute call this week work?\n\nBest,\nThe Sales Team",
      ),

      slackNode(
        "7",
        "Slack: Post re-engagement batch report",
        1480,
        ":wave: *Weekly Dormant Prospect Re-Engagement*\n\n*Total Dormant (60+ days):* {dormant_count}\n*Batch Sent:* {reengage_limit}\n*Priority:* {batch_priority}\n\n*Sample:* {prospect_name} at {company_name} (Score: {lead_score})\n*Angle:* {reengagement_angle}\n*Subject:* {email_subject}\n\nMonitor open/reply rates in the next 48 hours.",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log re-engagement campaign",
        1720,
        "Reengagement",
        `[["{prospect_name}","{prospect_email}","{company_name}","{lead_score}","{reengagement_angle}","{batch_priority}","{dormant_count}","${new Date().toISOString()}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Runs weekly to identify 60-day dormant prospects in HubSpot, selects personalized re-engagement angles via AI, suppresses recently contacted leads, sends tailored re-engagement emails with specific value propositions, posts batch reports to Slack, and logs all re-engagement campaigns to Google Sheets for tracking.",
  },

  // ===================================================================
  // 31. ABM Account Engagement Alerts — Pattern L
  // ===================================================================
  "ABM Account Engagement Alerts": {
    nodes: [
      trigger("ABM: Account Activity Surge Detected", "webhook_app", {
        source: "ABMPlatform",
        event: "account.engagement_surge",
        howItStarts:
          "Fires when an ABM platform (6sense, Demandbase, or custom tracking) detects a significant surge in engagement from a target account across multiple channels",
        setup:
          "Configure your ABM platform to POST engagement surge webhooks. Include account name, domain, engagement score, score change, channels active, intent topics, and buying stage signals.",
        inputFields: [],
      }),

      n(
        "2",
        "hitApi",
        "HubSpot: Fetch all account members and deal context",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/companies/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"domain","operator":"CONTAINS_TOKEN","value":"{{{1.body.account_domain}}}"}]}]',
              },
            ],
            variables: [
              {
                name: "results[0].properties.hs_object_id",
                customName: "hubspot_company_id",
                type: "string",
              },
              {
                name: "results[0].properties.name",
                customName: "account_name",
                type: "string",
              },
              {
                name: "results[0].properties.annualrevenue",
                customName: "account_arr",
                type: "number",
              },
              {
                name: "results[0].properties.hubspot_owner_id",
                customName: "account_owner_id",
                type: "string",
              },
              {
                name: "results[0].properties.num_associated_contacts",
                customName: "contacts_count",
                type: "number",
              },
            ],
          },
        },
      ),

      n("3", "runCode", "Compute engagement score and determine action", 520, {
        runCodeDetails: {
          code: `const webhookData = JSON.parse('{{{1.body}}}' || '{}');
const engagementScore = webhookData.engagement_score || 75;
const scoreChange = webhookData.score_change || 20;
const channels = webhookData.channels_active || 'web,email';
const isSignificantSurge = scoreChange > 15;
const accountTier = account_arr > 100000 ? 'tier_1' : account_arr > 25000 ? 'tier_2' : 'tier_3';
const actionType = isSignificantSurge && accountTier === 'tier_1' ? 'immediate_outreach' : isSignificantSurge ? 'priority_nurture' : 'monitor';
return { engagement_score: engagementScore, score_change: scoreChange, channels_active: channels, is_significant: isSignificantSurge, account_tier: accountTier, action_type: actionType };`,
          variables: [
            {
              name: "engagement_score",
              customName: "engagement_score",
              type: "number",
            },
            {
              name: "score_change",
              customName: "score_change",
              type: "number",
            },
            {
              name: "channels_active",
              customName: "channels_active",
              type: "string",
            },
            {
              name: "is_significant",
              customName: "is_significant",
              type: "boolean",
            },
            {
              name: "account_tier",
              customName: "account_tier",
              type: "string",
            },
            { name: "action_type", customName: "action_type", type: "string" },
          ],
        },
      }),

      aiNode(
        "4",
        "OpenAI: Interpret engagement signals and recommend play",
        760,
        "You are an ABM strategist. Interpret the engagement surge data, identify what the account is likely researching, and recommend the best outreach play for the account team.",
        "Interpret ABM engagement surge: Account: {account_name}. Engagement score: {engagement_score} (up {score_change}). Active channels: {channels_active}. Account tier: {account_tier}. Contacts in CRM: {contacts_count}. ARR: ${account_arr}. Return JSON with: intent_interpretation (what the account is likely researching), recommended_play (specific outreach tactic), urgency_window (how quickly to act), personalization_hook (what to lead with in outreach), risk_of_inaction (what happens if you don't act now).",
        [
          {
            field: "intent_interpretation",
            as: "intent_interpretation",
            type: "string",
          },
          { field: "recommended_play", as: "recommended_play", type: "string" },
          { field: "urgency_window", as: "urgency_window", type: "string" },
          {
            field: "personalization_hook",
            as: "personalization_hook",
            type: "string",
          },
          { field: "risk_of_inaction", as: "risk_of_inaction", type: "string" },
        ],
      ),

      n("5", "condition", "Is engagement score above action threshold?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "is_significant", operator: "equals", value: "true" },
          ],
        },
      }),

      slackNode(
        "6",
        "Slack: Alert account team with ABM intelligence",
        1240,
        ":chart_with_upwards_trend: *ABM Engagement Surge — {account_name}*\n\n*Tier:* {account_tier} | ARR: ${account_arr}\n*Engagement:* {engagement_score} (up {score_change} pts)\n*Channels:* {channels_active}\n*Contacts in CRM:* {contacts_count}\n\n*Intent:* {intent_interpretation}\n*Recommended Play:* {recommended_play}\n*Act Within:* {urgency_window}\n*Hook:* {personalization_hook}\n*Risk of Inaction:* {risk_of_inaction}\n\nAction: {action_type}",
      ),

      n("7", "hitApi", "HubSpot: Update account engagement status", 1480, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubapi.com/crm/v3/objects/companies/{hubspot_company_id}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"abm_engagement_score":"{engagement_score}","abm_last_surge_date":"' +
                new Date().toISOString() +
                '","abm_intent_signal":"{intent_interpretation}","abm_recommended_action":"{action_type}"}',
            },
          ],
          variables: [
            { name: "id", customName: "updated_company_id", type: "string" },
          ],
        },
      }),

      sheetsNode(
        "8",
        "Google Sheets: Log ABM engagement surge",
        1720,
        "ABM_Surges",
        `[["{account_name}","{account_tier}","{engagement_score}","{score_change}","{channels_active}","{intent_interpretation}","{action_type}","${new Date().toISOString()}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Detects ABM account engagement surges, fetches account context from HubSpot, computes engagement significance and account tier, uses AI to interpret intent signals and recommend outreach plays, alerts account teams via Slack with full ABM intelligence, updates account status in HubSpot, and logs all engagement surges to Google Sheets.",
  },

  // ===================================================================
  // 32. Buying Committee Contact Sync — Pattern K
  // ===================================================================
  "Buying Committee Contact Sync": {
    nodes: [
      trigger("LinkedIn Sales Nav: New Contact Saved", "webhook_app", {
        source: "LinkedIn",
        event: "lead.saved",
        howItStarts:
          "Fires when a sales rep saves a new contact from LinkedIn Sales Navigator, triggering automated CRM sync and buying committee mapping for the associated opportunity",
        setup:
          "Configure your LinkedIn Sales Navigator integration (via Zapier or direct API) to POST webhooks when leads are saved. Include contact name, title, company, LinkedIn URL, and the list/opportunity they were saved to.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract contact and determine committee role",
        280,
        "You are a B2B buying committee analyst. Parse the LinkedIn Sales Navigator lead data and determine the contacts role in the buying committee based on their title, seniority, and department.",
        "Analyze this LinkedIn Sales Nav saved lead: {{{1.body}}}. Return JSON with: contact_name, contact_title, contact_company, linkedin_url, seniority_level (c_suite/vp/director/manager/individual), department (sales/marketing/engineering/finance/ops/hr/it), buying_role (economic_buyer/technical_buyer/champion/influencer/end_user/blocker), committee_importance (critical/important/nice_to_have).",
        [
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "contact_title", as: "contact_title", type: "string" },
          { field: "contact_company", as: "contact_company", type: "string" },
          { field: "linkedin_url", as: "linkedin_url", type: "string" },
          { field: "buying_role", as: "buying_role", type: "string" },
          { field: "department", as: "department", type: "string" },
          {
            field: "committee_importance",
            as: "committee_importance",
            type: "string",
          },
          { field: "seniority_level", as: "seniority_level", type: "string" },
        ],
      ),

      n(
        "3",
        "hitApi",
        "HubSpot: Search for existing contact or create new",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"firstname":"{contact_name}","company":"{contact_company}","jobtitle":"{contact_title}","lead_source":"linkedin_sales_nav","buying_committee_role":"{buying_role}","hs_lead_status":"NEW"}',
              },
            ],
            variables: [
              { name: "id", customName: "hubspot_contact_id", type: "string" },
            ],
          },
        },
      ),

      n("4", "runCode", "Map contact to buying committee role", 760, {
        runCodeDetails: {
          code: `const isDecisionMaker = buying_role === 'economic_buyer' || (seniority_level === 'c_suite' || seniority_level === 'vp');
const engagementPriority = isDecisionMaker ? 'high' : committee_importance === 'critical' ? 'high' : 'standard';
const outreachStrategy = isDecisionMaker ? 'executive_connect' : buying_role === 'champion' ? 'value_alignment' : buying_role === 'technical_buyer' ? 'technical_deep_dive' : 'relationship_build';
return { is_decision_maker: isDecisionMaker, engagement_priority: engagementPriority, outreach_strategy: outreachStrategy };`,
          variables: [
            {
              name: "is_decision_maker",
              customName: "is_decision_maker",
              type: "boolean",
            },
            {
              name: "engagement_priority",
              customName: "engagement_priority",
              type: "string",
            },
            {
              name: "outreach_strategy",
              customName: "outreach_strategy",
              type: "string",
            },
          ],
        },
      }),

      n("5", "condition", "Is this a new decision maker?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "is_decision_maker", operator: "equals", value: "true" },
          ],
        },
      }),

      slackNode(
        "6",
        "Slack: Alert AE about new buying committee member",
        1240,
        ":busts_in_silhouette: *New Buying Committee Member — {contact_company}*\n\n*Contact:* {contact_name}\n*Title:* {contact_title} ({seniority_level})\n*Department:* {department}\n*Buying Role:* {buying_role}\n*Importance:* {committee_importance}\n*Decision Maker:* {is_decision_maker}\n*LinkedIn:* {linkedin_url}\n\n*Outreach Strategy:* {outreach_strategy}\n*Priority:* {engagement_priority}\n\nHubSpot: https://app.hubspot.com/contacts/{hubspot_contact_id}",
      ),

      n("7", "hitApi", "HubSpot: Associate contact with opportunity", 1480, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{hubspot_contact_id}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"buying_committee_role":"{buying_role}","buying_committee_importance":"{committee_importance}","outreach_strategy":"{outreach_strategy}","linkedin_url":"{linkedin_url}"}',
            },
          ],
          variables: [
            { name: "id", customName: "updated_contact_id", type: "string" },
          ],
        },
      }),

      sheetsNode(
        "8",
        "Google Sheets: Log buying committee mapping",
        1720,
        "Buying_Committee",
        `[["{contact_name}","{contact_title}","{contact_company}","{buying_role}","{department}","{seniority_level}","{committee_importance}","{outreach_strategy}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Syncs new contacts saved from LinkedIn Sales Navigator, determines buying committee roles via AI, creates HubSpot contacts with role mapping, identifies decision makers, alerts AEs via Slack when new decision makers are found, updates contact records with committee data, and logs all buying committee members to Google Sheets.",
  },

  // ===================================================================
  // 33. Pricing Request to Rep and Finance Alert — Pattern K
  // ===================================================================
  "Pricing Request to Rep and Finance Alert": {
    nodes: [
      trigger("Web Form: Pricing Request Submitted", "webhook_form", {
        source: "WebForm",
        event: "pricing.requested",
        howItStarts:
          "Fires when a prospect submits a pricing request form, often indicating late-stage buying intent that requires coordinated response from both sales and finance teams",
        setup:
          "Configure your pricing request form to POST submissions to this webhook. Include fields for name, email, company, number of users/seats needed, current solution, budget range, and timeline.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract pricing requirements and assess deal size",
        280,
        "You are a pricing operations specialist. Parse the pricing request form and extract all requirements needed to prepare an accurate quote. Assess the potential deal size and complexity.",
        "Extract from this pricing request form: {{{1.body}}}. Return JSON with: requester_email, requester_name, company_name, seats_needed (number), current_solution, budget_range, timeline (when they need to decide), use_case, estimated_deal_value (based on seats and typical pricing), needs_custom_pricing (boolean based on seat count > 100 or specific requirements).",
        [
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "seats_needed", as: "seats_needed", type: "number" },
          { field: "current_solution", as: "current_solution", type: "string" },
          { field: "budget_range", as: "budget_range", type: "string" },
          { field: "timeline", as: "timeline", type: "string" },
          {
            field: "estimated_deal_value",
            as: "estimated_deal_value",
            type: "number",
          },
          {
            field: "needs_custom_pricing",
            as: "needs_custom_pricing",
            type: "boolean",
          },
        ],
      ),

      n("3", "hitApi", "HubSpot: Lookup existing deal and contact", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{requester_email}"}]}]',
            },
          ],
          variables: [
            { name: "total", customName: "existing_contacts", type: "number" },
            {
              name: "results[0].properties.hs_object_id",
              customName: "hubspot_contact_id",
              type: "string",
            },
            {
              name: "results[0].properties.hubspot_owner_id",
              customName: "assigned_rep_id",
              type: "string",
            },
          ],
        },
      }),

      n(
        "4",
        "runCode",
        "Check pricing rules and determine approval path",
        760,
        {
          runCodeDetails: {
            code: `const standardPricingLimit = 50;
const enterpriseThreshold = 100;
const isEnterprise = seats_needed >= enterpriseThreshold;
const needsFinanceReview = needs_custom_pricing || estimated_deal_value > 50000 || isEnterprise;
const pricingTier = isEnterprise ? 'enterprise_custom' : seats_needed > standardPricingLimit ? 'growth_negotiable' : 'standard_self_serve';
const approvalPath = needsFinanceReview ? 'finance_review' : 'rep_authorized';
return { is_enterprise: isEnterprise, needs_finance_review: needsFinanceReview, pricing_tier: pricingTier, approval_path: approvalPath };`,
            variables: [
              {
                name: "is_enterprise",
                customName: "is_enterprise",
                type: "boolean",
              },
              {
                name: "needs_finance_review",
                customName: "needs_finance_review",
                type: "boolean",
              },
              {
                name: "pricing_tier",
                customName: "pricing_tier",
                type: "string",
              },
              {
                name: "approval_path",
                customName: "approval_path",
                type: "string",
              },
            ],
          },
        },
      ),

      n("5", "condition", "Does this request need custom pricing?", 1000, {
        conditionDetails: {
          conditions: [
            {
              field: "needs_custom_pricing",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),

      slackNode(
        "6",
        "Slack: Alert rep and finance with pricing request",
        1240,
        ":dollar: *New Pricing Request*\n\n*From:* {requester_name} ({requester_email})\n*Company:* {company_name}\n*Seats:* {seats_needed}\n*Current Solution:* {current_solution}\n*Budget:* {budget_range}\n*Timeline:* {timeline}\n*Estimated Value:* ${estimated_deal_value}\n\n*Pricing Tier:* {pricing_tier}\n*Approval Path:* {approval_path}\n*Finance Review:* {needs_finance_review}\n\nPlease prepare a quote within {timeline}.",
      ),

      emailNode(
        "7",
        "SendGrid: Confirm pricing request to prospect",
        1480,
        "{requester_email}",
        "We're preparing your pricing, {requester_name}!",
        "Hi {requester_name},\n\nThank you for your pricing inquiry! We received your request for {seats_needed} seats for {company_name}.\n\nBased on your requirements, here's what we're preparing:\n- A customized quote for your team size and use case\n- Comparison with your current solution ({current_solution})\n- ROI analysis specific to {company_name}\n\nWe'll have your detailed pricing ready within your timeline of {timeline}. A member of our team will reach out shortly to discuss any questions.\n\nBest regards,\nThe Sales Team",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log pricing request",
        1720,
        "Pricing_Requests",
        `[["{requester_name}","{requester_email}","{company_name}","{seats_needed}","{budget_range}","{estimated_deal_value}","{pricing_tier}","{timeline}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes pricing request form submissions, extracts requirements and estimates deal value via AI, looks up existing contacts in HubSpot, determines the pricing tier and approval path, alerts sales and finance teams via Slack, sends a confirmation email to the prospect, and logs all pricing requests to Google Sheets.",
  },

  // ===================================================================
  // 34. Trial Signup to Onboarding + Scoring — Pattern A
  // ===================================================================
  "Trial Signup to Onboarding + Scoring": {
    nodes: [
      trigger("Product: New Trial Account Created", "webhook_app", {
        source: "Product",
        event: "trial.created",
        howItStarts:
          "Fires when a new user signs up for a free trial, triggering automated CRM entry, enrichment, initial scoring, and a welcome onboarding sequence",
        setup:
          "Configure your product to POST a webhook when a new trial account is created. Include user email, name, company (if provided), signup source, plan selected, and any onboarding preferences.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract trial user details and assess fit",
        280,
        "You are a product-led growth onboarding specialist. Parse the trial signup webhook and extract user details. Assess initial fit based on available information to determine the appropriate onboarding track.",
        "Extract from this trial signup: {{{1.body}}}. Return JSON with: user_email, user_name, company_name, signup_source (organic/paid/referral/partner), selected_plan, use_case_mentioned, company_size_hint (from email domain or stated), is_business_email (boolean, false if gmail/yahoo/hotmail).",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "signup_source", as: "signup_source", type: "string" },
          { field: "selected_plan", as: "selected_plan", type: "string" },
          {
            field: "use_case_mentioned",
            as: "use_case_mentioned",
            type: "string",
          },
          {
            field: "is_business_email",
            as: "is_business_email",
            type: "boolean",
          },
        ],
      ),

      n("3", "hitApi", "HubSpot: Create contact and trial deal", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"email":"{user_email}","firstname":"{user_name}","company":"{company_name}","lead_source":"trial_signup","lifecyclestage":"lead","hs_lead_status":"NEW","trial_plan":"{selected_plan}"}',
            },
          ],
          variables: [
            { name: "id", customName: "hubspot_contact_id", type: "string" },
          ],
        },
      }),

      n("4", "hitApi", "Clearbit: Enrich trial user with company data", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={user_email}",
          headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
          variables: [
            {
              name: "person.employment.title",
              customName: "job_title",
              type: "string",
            },
            {
              name: "person.employment.seniority",
              customName: "seniority",
              type: "string",
            },
            {
              name: "company.name",
              customName: "enriched_company",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "employee_count",
              type: "number",
            },
            {
              name: "company.category.industry",
              customName: "industry",
              type: "string",
            },
          ],
        },
      }),

      n(
        "5",
        "runCode",
        "Compute initial trial score and onboarding track",
        1000,
        {
          runCodeDetails: {
            code: `const emailScore = is_business_email ? 20 : 5;
const companyScore = employee_count > 100 ? 25 : employee_count > 20 ? 15 : 5;
const seniorityScore = seniority === 'executive' || seniority === 'director' ? 20 : seniority === 'manager' ? 15 : 5;
const sourceScore = signup_source === 'referral' ? 15 : signup_source === 'paid' ? 10 : 5;
const trialScore = emailScore + companyScore + seniorityScore + sourceScore;
const onboardingTrack = trialScore > 60 ? 'high_touch' : trialScore > 35 ? 'guided' : 'self_serve';
return { trial_score: trialScore, onboarding_track: onboardingTrack, is_high_potential: trialScore > 60 };`,
            variables: [
              {
                name: "trial_score",
                customName: "trial_score",
                type: "number",
              },
              {
                name: "onboarding_track",
                customName: "onboarding_track",
                type: "string",
              },
              {
                name: "is_high_potential",
                customName: "is_high_potential",
                type: "boolean",
              },
            ],
          },
        },
      ),

      emailNode(
        "6",
        "SendGrid: Welcome email with onboarding resources",
        1240,
        "{user_email}",
        "Welcome to your trial, {user_name}! Here's how to get started",
        "Hi {user_name},\n\nWelcome aboard! Your trial is now active and ready to explore.\n\nHere's your personalized getting-started guide:\n\n1. Complete your profile and invite your team\n2. Follow our Quick Start tutorial (takes under 5 minutes)\n3. Try our top feature for {use_case_mentioned}\n\nAs someone in {industry}, you'll especially love:\n- Feature A that saves teams 10+ hours per week\n- Feature B that companies like {enriched_company} use daily\n- Feature C with built-in analytics and reporting\n\nNeed help? Reply to this email or check out our Help Center.\n\nLet's make the most of your trial!\n\nThe Onboarding Team",
      ),

      slackNode(
        "7",
        "Slack: Notify CS about high-potential trial signup",
        1480,
        ":star: *New Trial Signup — Score: {trial_score}*\n\n*User:* {user_name} ({user_email})\n*Title:* {job_title} ({seniority})\n*Company:* {enriched_company} ({industry}, {employee_count} emp)\n*Plan:* {selected_plan}\n*Source:* {signup_source}\n*Use Case:* {use_case_mentioned}\n*Onboarding Track:* {onboarding_track}\n*High Potential:* {is_high_potential}\n\nHubSpot: https://app.hubspot.com/contacts/{hubspot_contact_id}",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log trial signup with scoring",
        1720,
        "Trial_Signups",
        `[["{user_name}","{user_email}","{enriched_company}","{industry}","{trial_score}","{onboarding_track}","{signup_source}","{selected_plan}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes new trial signups, extracts user details and assesses fit via AI, creates HubSpot contacts, enriches with Clearbit person and company data, computes an initial trial score to determine the onboarding track, sends a personalized welcome email, notifies the CS team via Slack for high-potential signups, and logs all trials to Google Sheets.",
  },

  // ===================================================================
  // 35. Win/Loss Interview Scheduler + Notes — Pattern J
  // ===================================================================
  "Win Loss Interview Scheduler + Notes": {
    nodes: [
      trigger("HubSpot: Deal Closed (Won or Lost)", "webhook_app", {
        source: "HubSpot",
        event: "deal.propertyChange",
        howItStarts:
          "Fires when a HubSpot deal moves to Closed-Won or Closed-Lost, triggering the win/loss interview process to capture valuable feedback for product and sales improvement",
        setup:
          "In HubSpot > Settings > Integrations > Webhooks, subscribe to deal property changes filtered to dealstage in ('closedwon','closedlost'). Include deal ID, amount, contact associations, and all deal properties.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract deal context and determine interview eligibility",
        280,
        "You are a sales insights researcher. Parse the HubSpot deal closure webhook and determine if this deal qualifies for a win/loss interview based on deal size, strategic importance, and potential learning value.",
        "Extract from this HubSpot deal closure: {{{1.body}}}. Return JSON with: deal_id, deal_name, deal_amount, deal_outcome (won/lost), contact_email, contact_name, company_name, deal_duration_days, rep_name, loss_reason (if lost), is_strategic (boolean based on amount > $25000 or competitive deal), interview_priority (high/medium/low).",
        [
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "number" },
          { field: "deal_outcome", as: "deal_outcome", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "is_strategic", as: "is_strategic", type: "boolean" },
          {
            field: "interview_priority",
            as: "interview_priority",
            type: "string",
          },
        ],
      ),

      n("3", "runCode", "Determine interview eligibility and type", 520, {
        runCodeDetails: {
          code: `const isLost = deal_outcome === 'lost';
const isStrategicWin = deal_outcome === 'won' && is_strategic;
const isEligible = isLost || isStrategicWin;
const interviewType = isLost ? 'loss_analysis' : 'win_story';
const interviewGoal = isLost ? 'Understand loss reasons and competitive landscape' : 'Capture success story and product value validation';
const urgency = isLost && deal_amount > 50000 ? 'high' : 'standard';
return { is_eligible: isEligible, interview_type: interviewType, interview_goal: interviewGoal, interview_urgency: urgency };`,
          variables: [
            { name: "is_eligible", customName: "is_eligible", type: "boolean" },
            {
              name: "interview_type",
              customName: "interview_type",
              type: "string",
            },
            {
              name: "interview_goal",
              customName: "interview_goal",
              type: "string",
            },
            {
              name: "interview_urgency",
              customName: "interview_urgency",
              type: "string",
            },
          ],
        },
      }),

      n(
        "4",
        "condition",
        "Is this a strategic closed-lost or strategic win?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "is_eligible", operator: "equals", value: "true" },
            ],
          },
        },
      ),

      n("5", "hitApi", "Calendly: Create scheduling link for interview", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.calendly.com/scheduling_links",
          headers: [
            { key: "Authorization", value: "Bearer {calendly_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "max_event_count", value: "1" },
            {
              key: "owner",
              value:
                "https://api.calendly.com/event_types/{win_loss_event_type_id}",
            },
            { key: "owner_type", value: "EventType" },
          ],
          variables: [
            {
              name: "resource.booking_url",
              customName: "scheduling_url",
              type: "string",
            },
          ],
        },
      }),

      emailNode(
        "6",
        "SendGrid: Send interview invitation to contact",
        1240,
        "{contact_email}",
        "Would you share your feedback with us, {contact_name}?",
        "Hi {contact_name},\n\nThank you for the time you invested in evaluating us for {company_name}. Regardless of the outcome, your perspective is incredibly valuable to us.\n\nWe'd love to schedule a brief 20-minute conversation to hear your honest feedback. Your insights directly influence our product roadmap and how we serve customers.\n\nAs a thank you for your time, we'll send you a $50 gift card.\n\nSchedule at your convenience: {scheduling_url}\n\nNo sales pitch — just a genuine desire to learn and improve.\n\nWarmly,\nThe Product Team",
      ),

      slackNode(
        "7",
        "Slack: Notify PM team about interview request",
        1480,
        ":microphone: *Win/Loss Interview Requested*\n\n*Deal:* {deal_name} | ${deal_amount}\n*Outcome:* {deal_outcome}\n*Company:* {company_name}\n*Contact:* {contact_name} ({contact_email})\n*Interview Type:* {interview_type}\n*Goal:* {interview_goal}\n*Priority:* {interview_priority}\n*Urgency:* {interview_urgency}\n\n*Scheduling Link:* {scheduling_url}\n\nPlease monitor for the booking and prepare interview questions.",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log interview request and status",
        1720,
        "Win_Loss_Interviews",
        `[["{deal_name}","{company_name}","{deal_amount}","{deal_outcome}","{contact_email}","{interview_type}","{interview_priority}","{scheduling_url}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Triggered when HubSpot deals close (won or lost), extracts deal context and determines interview eligibility via AI, creates Calendly scheduling links for qualified deals, sends interview invitations to contacts with incentives, notifies the PM team via Slack to prepare, and logs all interview requests to Google Sheets for tracking.",
  },
};
